import { useState, useRef } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2, Trash2, Info } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from './Button';
import { Alert } from './Alert';
import type { UploadedFile } from '@/types';

interface FileUploadProps {
  files: UploadedFile[];
  onUpload: (file: File) => Promise<void>;
  onDelete: (fileId: string) => Promise<void>;
  maxFiles: number | null;  // null = unlimited
  isLoading?: boolean;
  className?: string;
}

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.csv', '.xlsx', '.xls', '.doc', '.docx', '.png', '.jpg', '.jpeg'];

export function FileUpload({ 
  files, 
  onUpload, 
  onDelete, 
  maxFiles, 
  isLoading: _isLoading = false,
  className 
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const canUpload = maxFiles === null || files.length < maxFiles;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (canUpload) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canUpload) return;
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) await uploadFile(droppedFile);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) await uploadFile(selectedFile);
    if (inputRef.current) inputRef.current.value = '';
  };

  const uploadFile = async (file: File) => {
    setError(null);
    
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    setDeletingId(fileId);
    try {
      await onDelete(fileId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'clean': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'infected': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <FileText className="w-4 h-4 text-text-muted" />;
    }
  };

  return (
    <div className={clsx('space-y-4', className)}>
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
      
      {/* Upload Zone */}
      {canUpload && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={clsx(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
            isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
            uploading && 'opacity-50 pointer-events-none'
          )}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />
          {uploading ? (
            <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
          ) : (
            <Upload className="w-8 h-8 mx-auto text-text-muted" />
          )}
          <p className="mt-2 text-sm text-text-secondary">
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {maxFiles === null ? 'Unlimited uploads' : `${files.length}/${maxFiles} files`}
          </p>
        </div>
      )}

      {/* Provider Transparency Notice */}
      {canUpload && (
        <div className="flex items-start gap-2 px-3 py-2 text-xs text-text-muted bg-surface/50 border border-border/50 rounded-lg">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Uploaded files may be processed by AI providers including OpenAI, Anthropic, Google, and DeepSeek to power research analysis. Files are encrypted in transit and at rest.
          </span>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-text-secondary">Uploaded Files</h4>
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-surface border border-border rounded-lg"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(file.status)}
                <div>
                  <p className="text-sm font-medium truncate max-w-[200px]">{file.original_filename}</p>
                  <p className="text-xs text-text-muted">
                    {file.file_type}
                    {file.processing_status === 'processing' && ' \u00b7 Processing...'}
                    {file.processing_status === 'completed' && ' \u00b7 Ready'}
                    {file.processing_status === 'failed' && ' \u00b7 Processing failed'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(file.id)}
                disabled={deletingId === file.id}
              >
                {deletingId === file.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 text-red-500" />
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

